/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  getExternalAppointmentConfig,
  getTimeAvailability,
} from 'api/AppointmentController';
import { getRepsAndOpenHousesForListing } from 'api/ListingController';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { addHours, addMilliseconds, addMinutes, isPast } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import styled from 'styled-components';
import { SHOWING_PROVIDERS_CONST } from '@bb/common';
import { Text } from 'components/Typography/Text';
import { SecondaryText, Spin } from '@brokerbay/design-components';
import { message, Title, theme } from 'components';
import { getTimezoneCode } from 'utils';
import { Timezone } from '@bb/domain/dist/types';
import type { GetExternalConfig } from '@bb/domain/dist/types/dto/AppointmentController/getExternalConfig';
import { useBreakpoint } from 'hooks';
import { OpenHouse } from '@bb/domain/dist/types/Listing';
import { AuthContext } from 'contexts';
import TourEditContext from '../TourEditContext';
import TourListingContext from '../TourListingContext';
import TimeSelectorItem from './TimeSelectorItem';
import SVGNoMoreShowings from './SVGNoMoreShowings';
import { formatToZonedTime, checkIsIOS } from '../utils';
import ModalMobileHeader from './ModalMobileHeader';
import ModalMobileFooter from './ModalMobileFooter';
import MobileFriendlyTooltip from '../MobileFriendlyTooltip';
import { useWindowHeight } from '../hooks';

const EARLY_TOMORROW = ['12:15 AM', '12:30 AM', '12:45 AM'];

const DURATION_INTERVAL_MINUTES = 15;
const BASE_DURATION_OPTIONS = [15, 30, 45, 60];
const DEFAULT_EXTERNAL_CONFIG: GetExternalConfig = {
  noMoreShowings: false,
  maxDuration: 60,
  isVirtualTourEnabled: false,
  displayedToSeller: false,
  autoConfirm: false,
  preBookingInformation: undefined,
  showingProviderAdditionalInfo: undefined,
};

const normalizeDurationForInterval = (
  value: number | undefined,
  mode: 'ceil' | 'floor',
) => {
  if (value == null) return undefined;
  if (mode === 'ceil') {
    return (
      Math.ceil(value / DURATION_INTERVAL_MINUTES) * DURATION_INTERVAL_MINUTES
    );
  }
  return (
    Math.floor(value / DURATION_INTERVAL_MINUTES) * DURATION_INTERVAL_MINUTES
  );
};

type ShowingProviderAdditionalInfo =
  GetExternalConfig['showingProviderAdditionalInfo'];

const getShowingDurationBounds = (
  showingProviderAdditionalInfo?: ShowingProviderAdditionalInfo,
) => {
  if (!showingProviderAdditionalInfo) {
    return {};
  }

  const minDuration = normalizeDurationForInterval(
    showingProviderAdditionalInfo.minShowingLengthMinutes,
    'ceil',
  );
  const maxDuration = normalizeDurationForInterval(
    showingProviderAdditionalInfo.maxShowingLengthMinutes,
    'floor',
  );

  // Handle edge case: when range is too narrow (min > max after normalization)
  // Use the raw max value as the single valid option to respect exact provider constraints
  if (minDuration != null && maxDuration != null && minDuration > maxDuration) {
    const singleOption = showingProviderAdditionalInfo.maxShowingLengthMinutes!;
    return { minDuration: singleOption, maxDuration: singleOption };
  }

  return { minDuration, maxDuration };
};

const getDurationOptions = (minDuration: number, maxDuration: number) => {
  // If no bounds were parsed (both 0 or undefined), default to standard options
  const effectiveMin = minDuration || 15;
  const effectiveMax = maxDuration || 60;

  // If min and max are the same (narrow range with single custom duration)
  // return that specific duration even if it's not a standard interval
  if (
    effectiveMin === effectiveMax &&
    effectiveMin % DURATION_INTERVAL_MINUTES !== 0
  ) {
    return [effectiveMin];
  }

  let maxBasedOptions: number[];
  if (effectiveMax >= 60) {
    // When min is 30+, show [30, 45, 60] to include 45 in the range
    maxBasedOptions = effectiveMin >= 30 ? [30, 45, 60] : [15, 30, 60];
  } else if (effectiveMax >= 45) {
    maxBasedOptions = [15, 30, 45];
  } else {
    maxBasedOptions = [15, 30];
  }

  const inRangeOptions = maxBasedOptions.filter(
    duration => duration >= effectiveMin && duration <= effectiveMax,
  );

  if (inRangeOptions.length > 0) return inRangeOptions;

  return BASE_DURATION_OPTIONS.filter(
    duration => duration >= effectiveMin && duration <= effectiveMax,
  );
};

const isTimeOpenHouse = (time: Date, openHouses: OpenHouse[]) => {
  let isOpenHouse = false;
  let isOpenHouseStart = false;
  let isOpenHouseEnd = false;

  for (let i = 0; i < openHouses.length; i++) {
    const openHouse = openHouses[i];
    if (new Date(openHouse.timeTo).getTime() === time.getTime()) {
      isOpenHouse = true;
      isOpenHouseEnd = true;
      break;
    }

    if (new Date(openHouse.timeFrom).getTime() === time.getTime()) {
      isOpenHouse = true;
      isOpenHouseStart = true;
      break;
    }

    if (
      new Date(openHouse.timeFrom).getTime() < time.getTime() &&
      time.getTime() < new Date(openHouse.timeTo).getTime()
    ) {
      isOpenHouse = true;
      break;
    }
  }

  return { isOpenHouse, isOpenHouseStart, isOpenHouseEnd };
};
const getClosestDuration = (
  durationOptions: number[],
  startTime?: Date,
  endTime?: Date,
) => {
  const fallbackDuration = durationOptions[durationOptions.length - 1] ?? 60;

  if (!startTime || !endTime) {
    return fallbackDuration;
  }

  const duration = Math.round(
    (endTime.getTime() - startTime.getTime()) / (1000 * 60),
  );

  const closestDuration = durationOptions.reduce(
    (prev, curr) =>
      Math.abs(curr - duration) < Math.abs(prev - duration) ? curr : prev,
    durationOptions[0] ?? fallbackDuration,
  );
  return closestDuration;
};

interface TimeSelectorDropdownOverlayProps {
  listingId: string;
  listingTimezone: Timezone;
  startTime?: Date;
  endTime?: Date;
  itemId: string;
  triggerRef?: React.MutableRefObject<any>;
  fromModal?: boolean;
  toggleDropdownVisibility?: () => void;
  setDropdownVisible?: React.Dispatch<React.SetStateAction<boolean>>;
}

const TimeSelectorDropdownOverlay: React.FC<
  TimeSelectorDropdownOverlayProps
> = ({
  listingId,
  listingTimezone,
  startTime,
  endTime,
  itemId,
  fromModal,
  toggleDropdownVisibility,
  setDropdownVisible,
  triggerRef,
}) => {
  const { tour, setTourItemTime, modalFooterRef } = useContext(TourEditContext);
  const { auth } = useContext(AuthContext);
  const { mobileFooterRef, setSelectedTimes } = useContext(TourListingContext);
  const [availableTimes, setAvailableTimes] = useState<Record<string, boolean>>(
    {},
  );
  const [isLoading, setIsLoading] = useState(true);
  const [showingTimesHeight, setShowingTimesHeight] = useState(0);

  const [selectedDuration, setSelectedDuration] = useState(15); // in minutes
  const [externalConfig, setExternalConfig] = useState<GetExternalConfig>(
    DEFAULT_EXTERNAL_CONFIG,
  );
  const [openHouses, setOpenHouses] = useState<OpenHouse[]>([]);
  const [showingProvider, setShowingProvider] = useState<string>();
  const { lg } = useBreakpoint();
  const isIOS = !!checkIsIOS();
  const windowHeight = useWindowHeight();

  const containerRef = useRef(null);
  const timezoneRef = useRef(null);
  const durationsRef = useRef(null);

  const timezoneOffset = timezoneRef?.current?.getBoundingClientRect().bottom;
  const durationsOffset = durationsRef?.current?.getBoundingClientRect().bottom;

  const durationBounds = useMemo(() => {
    const fallbackMax = Number(externalConfig?.maxDuration ?? 60);
    const providerBounds = getShowingDurationBounds(
      externalConfig?.showingProviderAdditionalInfo,
    );

    const result = {
      minDuration: providerBounds.minDuration ?? 15,
      maxDuration: providerBounds.maxDuration ?? fallbackMax,
    };

    return result;
  }, [externalConfig]);

  const durationOptions = useMemo(() => {
    const result = getDurationOptions(
      durationBounds.minDuration,
      durationBounds.maxDuration,
    );
    return result;
  }, [durationBounds.maxDuration, durationBounds.minDuration]);

  const hasProviderDurationBounds = useMemo(() => {
    const result =
      Boolean(externalConfig?.showingProviderAdditionalInfo) &&
      showingProvider &&
      showingProvider !== SHOWING_PROVIDERS_CONST.SUPRA;

    return result;
  }, [externalConfig?.showingProviderAdditionalInfo, showingProvider]);

  const displayedDurationOptions = useMemo(() => {
    const result = hasProviderDurationBounds ? durationOptions : [15, 30, 60];

    if (!hasProviderDurationBounds) {
      return result;
    }

    const { minDuration, maxDuration } = durationBounds;

    // If we have a custom non-standard duration (narrow range), use it as-is
    if (
      minDuration === maxDuration &&
      minDuration % DURATION_INTERVAL_MINUTES !== 0
    ) {
      return result; // result already contains the custom duration from getDurationOptions
    }

    // If min duration is greater than 45, only show 60m
    if (minDuration > 45) {
      return [60];
    }

    // If min duration is 45 and max is at least 60, show [45, 60]
    if (minDuration === 45 && maxDuration >= 60) {
      return [45, 60];
    }

    return result;
  }, [durationOptions, hasProviderDurationBounds, durationBounds]);

  const selectableDurationOptions = useMemo(() => {
    if (hasProviderDurationBounds) {
      return displayedDurationOptions;
    }
    // For non-external providers, legacy max duration still controls availability.
    const legacyMaxDuration = Number(externalConfig.maxDuration);
    return displayedDurationOptions.filter(min => min <= legacyMaxDuration);
  }, [
    displayedDurationOptions,
    externalConfig.maxDuration,
    hasProviderDurationBounds,
  ]);

  const handleMouseClicks = useCallback(
    ({ target }: MouseEvent) => {
      if (
        setDropdownVisible &&
        !containerRef.current.contains(target) &&
        target !== triggerRef?.current
      ) {
        setDropdownVisible(false);
      }
    },
    [setDropdownVisible, triggerRef],
  );

  const isTimeValid = useCallback(
    (
      startTime_: Date,
      duration: number,
      listingTimezone_: Timezone,
    ): {
      isValid: boolean;
      passedTourDate?: boolean;
    } => {
      const showingEndTime = addMinutes(startTime_, duration);
      let currentTime = new Date(startTime_);

      const passedTourDate = EARLY_TOMORROW.includes(
        formatToZonedTime(showingEndTime, 'h:mm a', listingTimezone_),
      );

      while (currentTime.getTime() < showingEndTime.getTime()) {
        const timestamp =
          availableTimes[
            formatToZonedTime(currentTime, 'h:mm a', listingTimezone_)
          ];
        if (!timestamp) {
          return {
            isValid: false,
            passedTourDate,
          };
        }

        currentTime = addMinutes(currentTime, 15);
      }
      return { isValid: true };
    },
    [availableTimes],
  );

  const scrollToAvailableTime = useCallback(node => {
    if (node !== null) {
      node.scrollIntoView();
    }
  }, []);

  const handleDurationChange = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    const selectedDurationTemp = parseInt(
      e.currentTarget.getAttribute('data-duration'),
      10,
    );

    if (startTime) {
      const { isValid, passedTourDate } = isTimeValid(
        startTime,
        selectedDurationTemp,
        listingTimezone,
      );
      if (isValid) {
        const newEndTime = addMinutes(startTime, selectedDurationTemp);
        setSelectedDuration(selectedDurationTemp);
        setTourItemTime(startTime, newEndTime, itemId);
        if (fromModal) {
          setSelectedTimes([startTime, newEndTime]);
        }
      }
      if (passedTourDate) {
        message.warn('Time cannot be selected past tour date.');
      }
    } else {
      setSelectedDuration(selectedDurationTemp);
    }
  };

  const { timesOfDay, firstAvailableTime } = useMemo(() => {
    // populating timesOfDay based on the item's timezone and finding the firstAvailableTime between timesOfDay
    if (tour?.date && listingTimezone && availableTimes) {
      let firstAvailableTimeTemp;
      const userTimezone =
        auth.org.addresses[parseInt(auth.location, 10)][
          parseInt(auth.address, 10)
        ].timezone;
      const userTimezoneOffset = getTimezoneOffset(userTimezone, tour.date);
      const listingTimezoneOffset = getTimezoneOffset(
        listingTimezone,
        tour.date,
      );
      const dayStartListingTimezone = addMilliseconds(
        tour.date,
        userTimezoneOffset - listingTimezoneOffset,
      );
      const dayEndListingTimezone = addHours(dayStartListingTimezone, 24);
      let timeOfDay = new Date(dayStartListingTimezone.getTime());
      const timesOfDayTemp: Date[] = [];
      while (timeOfDay.getTime() < dayEndListingTimezone.getTime()) {
        timesOfDayTemp.push(timeOfDay);
        if (
          !firstAvailableTimeTemp &&
          !isPast(timeOfDay) &&
          availableTimes[
            formatToZonedTime(timeOfDay, 'h:mm a', listingTimezone)
          ]
        ) {
          firstAvailableTimeTemp = timeOfDay;
        }
        timeOfDay = addMinutes(timeOfDay, 15);
      }
      return {
        timesOfDay: timesOfDayTemp,
        firstAvailableTime: firstAvailableTimeTemp,
      };
    }
  }, [
    tour?.date,
    availableTimes,
    auth.address,
    auth.location,
    auth.org.addresses,
    listingTimezone,
  ]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [listingData, availableTimesData, externalConfigData] =
          await Promise.all([
            getRepsAndOpenHousesForListing(listingId, {
              includeShowingProvider: true,
            }),
            getTimeAvailability({
              listingId,
              dateStr: formatToZonedTime(
                tour.date,
                'yyyy-MM-dd',
                listingTimezone,
              ),
              checkUnavailableDays: true,
            }),
            getExternalAppointmentConfig(listingId),
          ]);

        setExternalConfig(externalConfigData ?? DEFAULT_EXTERNAL_CONFIG);
        setAvailableTimes(availableTimesData);
        setOpenHouses(listingData.openHouses);
        setShowingProvider(listingData.showingProvider);
      } catch (err) {
        message.error('Error fetching the available showing times');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    listingId,
    listingTimezone,
    tour.date,
    auth?.id,
    auth.address,
    auth.location,
    auth.org.addresses,
  ]);

  useEffect(() => {
    if (!selectableDurationOptions.length) return;

    // Do not auto-correct when a time range has not been chosen yet.
    // This keeps the default 15m selectable/usable for initial interaction.
    if (!startTime || !endTime) {
      return;
    }

    const closestDuration = getClosestDuration(
      selectableDurationOptions,
      startTime,
      endTime,
    );
    const currentDuration = Math.round(
      (endTime.getTime() - startTime.getTime()) / (1000 * 60),
    );

    if (selectedDuration !== closestDuration) {
      setSelectedDuration(closestDuration);
    }

    if (currentDuration !== closestDuration) {
      const correctedEndTime = addMinutes(startTime, closestDuration);
      setTourItemTime(startTime, correctedEndTime, itemId);

      if (fromModal) {
        setSelectedTimes([startTime, correctedEndTime]);
      }
    }
  }, [
    selectableDurationOptions,
    startTime,
    endTime,
    selectedDuration,
    fromModal,
    itemId,
    setSelectedTimes,
    setTourItemTime,
  ]);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseClicks);

    return () => {
      document.removeEventListener('mouseup', handleMouseClicks);
    };
  }, [handleMouseClicks]);

  useEffect(() => {
    const modalFooterNode = modalFooterRef?.current;

    const tourListingFooterNode = mobileFooterRef?.current
      ?.parentNode as HTMLElement;

    const footerNode = tourListingFooterNode || modalFooterNode || null;
    const headerOffset = externalConfig?.noMoreShowings
      ? durationsOffset
      : timezoneOffset;

    if (!headerOffset || !footerNode) return;

    const containerHeight =
      windowHeight - headerOffset - footerNode.clientHeight;

    setShowingTimesHeight(containerHeight);
  }, [
    timezoneOffset,
    durationsOffset,
    mobileFooterRef,
    modalFooterRef,
    externalConfig?.noMoreShowings,
    windowHeight,
  ]);

  return (
    <DropdownContainer
      ref={containerRef}
      $lg={lg}
      $fromModal={fromModal}
      $isIOS={isIOS}
    >
      {!lg && !fromModal && (
        <ModalMobileHeader
          title="Pick Time"
          handleBackIconClick={toggleDropdownVisibility}
        />
      )}
      <DurationsContainer $lg={lg} ref={durationsRef}>
        {displayedDurationOptions.map(min => {
          const isUnavailableLegacyOption =
            !hasProviderDurationBounds &&
            min > Number(externalConfig?.maxDuration ?? 60);

          if (isUnavailableLegacyOption) {
            return (
              <MobileFriendlyTooltip
                key={min}
                title="This duration is unavailable"
                // @ts-ignore antd didn't type this properly
                zIndex={4001}
              >
                <DurationItem data-duration={min} $unavailable>
                  {`${min}m`}
                </DurationItem>
              </MobileFriendlyTooltip>
            );
          }

          return (
            <DurationItem
              key={min}
              data-duration={min}
              $selected={selectedDuration === min}
              onClick={handleDurationChange}
            >
              {`${min}m`}
            </DurationItem>
          );
        })}
      </DurationsContainer>
      {!externalConfig?.noMoreShowings && (
        <div ref={timezoneRef}>
          <TimezoneText color={theme.gray8} textAlign="center" $lg={lg}>
            Times reflect the listing’s timezone (
            {getTimezoneCode(listingTimezone)})
          </TimezoneText>
        </div>
      )}
      <ShowingTimesContainer $lg={lg} $height={showingTimesHeight}>
        {isLoading && (
          <StyledSpinnerContainer>
            <Spin />
          </StyledSpinnerContainer>
        )}
        {!isLoading && (
          <>
            {externalConfig.noMoreShowings && (
              <StyledNoMoreShowings $lg={lg} $height={showingTimesHeight}>
                <SVGNoMoreShowings />
                <StyledTitle level={3} textAlign="center">
                  Showings are turned off for this listing
                </StyledTitle>
                <StyledSecondaryText textAlign="center">
                  For more information, please call the listing’s brokerage
                </StyledSecondaryText>
              </StyledNoMoreShowings>
            )}
            {!externalConfig.noMoreShowings && (
              <>
                {timesOfDay?.map(time => (
                  <TimeSelectorItem
                    ref={
                      time.getTime() === firstAvailableTime?.getTime()
                        ? scrollToAvailableTime
                        : undefined
                    }
                    key={time.toISOString()}
                    time={time}
                    listingTimezone={listingTimezone}
                    selectedDuration={selectedDuration}
                    isStartTime={time.getTime() === startTime?.getTime()}
                    timePassed={isPast(time)}
                    isSelected={
                      time.getTime() >= startTime?.getTime() &&
                      time.getTime() < endTime?.getTime()
                    }
                    itemId={itemId}
                    isTimeValid={isTimeValid}
                    isTimeOpenHouse={isTimeOpenHouse(time, openHouses)}
                    unavailable={
                      !availableTimes[
                        formatToZonedTime(time, 'h:mm a', listingTimezone)
                      ]
                    }
                    fromModal={fromModal}
                  />
                ))}
              </>
            )}
          </>
        )}
      </ShowingTimesContainer>
      {!lg && !fromModal && (
        <ModalMobileFooter
          buttonText="Done"
          onButtonClick={toggleDropdownVisibility}
        />
      )}
    </DropdownContainer>
  );
};

export default TimeSelectorDropdownOverlay;

const DropdownContainer = styled.div<{
  $lg: boolean;
  $fromModal?: boolean;
  $isIOS: boolean;
}>`
  display: flex;
  flex-direction: column;
  background-color: white;
  ${({ $lg }) =>
    $lg &&
    'box-shadow: 0 3px 6px -4px #0000001f, 0 6px 16px #00000014, 0 9px 28px 8px #0000000d;'};
  ${({ $lg, $fromModal }) => !$lg && !$fromModal && 'height:100vh;'};
  ${({ $lg, $fromModal, $isIOS }) =>
    !$lg && !$fromModal && $isIOS && 'height: -webkit-fill-available;'};
  width: ${({ $fromModal, $lg }) =>
    $fromModal && $lg ? '100%' : $lg ? '243px' : '100vw'};
`;

const ShowingTimesContainer = styled.div<{
  $lg: boolean;
  $height: number;
}>`
  max-height: calc((100vh / 2 - 40px));
  ${({ $lg }) => !$lg && `max-height: 100vh;`}
  ${({ $lg }) => $lg && 'height: 400px'};
  ${({ $lg, $height }) => !$lg && $height && `height: ${$height}px`};
  overflow-y: scroll;
`;

const DurationsContainer = styled.div<{ $lg: boolean }>`
  display: flex;
  flex-direction: row;
  background-color: white;
  ${({ $lg }) => !$lg && 'padding: 1.4rem;'};
`;

const DurationItem = styled.div<{
  $selected?: boolean;
  $unavailable?: boolean;
}>`
  cursor: pointer;
  text-align: center;
  flex-grow: 1;
  font-size: 14px;
  padding: 5px 0px;
  box-shadow:
    inset 1px 0px 0px #d9d9d9,
    inset -1px 0px 0px #d9d9d9,
    inset 0px 1px 0px #d9d9d9,
    inset 0px -1px 0px #d9d9d9;

  ${({ $selected }) => $selected && `color: #2C6EF2`}
  ${({ $unavailable }) =>
    $unavailable &&
    `color: rgba(0, 0, 0, 0.25); background-color: #F5F5F5; cursor: default;`}
`;

const TimezoneText = styled(Text)<{ $lg: boolean }>`
  box-shadow: inset 0px -1px 0px #f0f0f0;
  padding: 1rem 0;
  font-size: 1.2rem;
  background-color: white;
  ${({ $lg }) => !$lg && 'border-top: 1px solid #f0f0f0;'};
`;

const StyledNoMoreShowings = styled.div<{
  $lg: boolean;
  $height: number;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  ${({ $lg }) => $lg && 'height: 400px'};
  ${({ $lg, $height }) => !$lg && $height && `height: ${$height}px`};
`;

const StyledTitle = styled(Title)`
  margin: 1.6rem 0 !important;
  width: 18rem;
`;

const StyledSecondaryText = styled(SecondaryText)`
  font-size: 12px;
  width: 14.4rem !important;
`;

const StyledSpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
`;
